import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import ContainerPage from "../../components/ContainerPage/ContainerPage"
import PageTitle from "../../components/PageTitle/PageTitle"
import TableFooter from "../../components/Pagination/TableFooter"

import TableContactList from "../Contacts/Table/Contact/TableContactList"
import { useDispatch, useSelector } from "react-redux"
import { REQUEST_ERRORS_LISTS } from "../../constants/requestErrorFlags"
import { fetchContactsReq, getContactByFilterList, getContactStatuses, getLeadDispositions } from "../../features/customer/customerSlice"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import DebounceInput from "../../components/DebouncedSearch/DebouncedSearch"
import PlusIcon from "../../assets/images/icons/PlusIcon.svg"
import { LuRotateCw } from "react-icons/lu"
import Button from "../../components/Button/Button"
import { useNavigate } from "react-router-dom"
import { StorageGetItem, USER } from "../../lib/storage"
import Filters from "../../components/Filters/Filters"
import AppliedFilters from "../../components/AppliedFilters/AppliedFilters"
import { initQueryData } from "../../components/Filters/helper"
import useURLState from "../../hooks/useURLState"
import StatusTabs from "../../components/StatusTabs/StatusTabs"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { getListCache, setListCache, clearListCache, buildCacheKey } from "../../utils/listCache"
import { downloadCustomersCsv } from "../../features/customersExport/customersExport"
import { TABLE_CARD } from "../../components/Table/tableCardClass"
import { handleTableId } from "../../helpers/helper"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { LEADS_DEBT_AMOUNT, LEAD_DISPOSITION_PIPELINE } from "../../config/featureFlagKeys"
import {
  ACTIVE_VIEW_TAB,
  ALL_VIEW_TAB,
  ALL_VIEW,
  hasLeadStatusFilter,
  requestDispositionView,
} from "../../components/LeadStatus/leadStatus"

const LEAD_STATUS_VALUES = ['newLead', 'duplicate']

const Leads = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const debtAmountOn = useSelector(selectFeatureFlag(LEADS_DEBT_AMOUNT))
  const pipelineOn = useSelector(selectFeatureFlag(LEAD_DISPOSITION_PIPELINE))
  const { triggerBar } = useMyContextLoading()
  const listStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_LISTS))
  const listStrictRef = useRef(listStrict)
  listStrictRef.current = listStrict

  const {
    page,
    setPage,
    limit,
    search,
    filters,
    filterListId,
    sortField,
    sortOrder,
    status,
    disposition,
    dispositionView,
    setMultiple,
  } = useURLState({
    page: { type: 'number', default: 1 },
    limit: { type: 'number', default: 10 },
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] },
    filterListId: { type: 'string', default: '' },
    sortField: { type: 'string', default: '' },
    sortOrder: { type: 'string', default: '' },
    status: { type: 'string', default: '' },
    disposition: { type: 'string', default: '' },
    dispositionView: { type: 'string', default: '' },
  })

  const [pageCount, setPageCount] = useState('')
  const [totalCount, setTotalCount] = useState(0)
  const [handleDelete, setHandleDelete] = useState('')
  const [contacts, setContacts] = useState([])
  const [contactsLoading, setContactsLoading] = useState(true)
  const [currentUser, setCurrentUser] = useState(null)
  const [filterData, setFilterData] = useState({ filters: [], utils: [] })
  const [statusCounts, setStatusCounts] = useState({})
  const [userStatuses, setUserStatuses] = useState([])
  const [statusesLoaded, setStatusesLoaded] = useState(false)
  const [countsLoaded, setCountsLoaded] = useState(false)
  const [leadDispositions, setLeadDispositions] = useState([])
  const [dispositionCounts, setDispositionCounts] = useState({})
  const [dispositionsLoaded, setDispositionsLoaded] = useState(false)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const [isExporting, setIsExporting] = useState(false)

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters])
  const viewParam = requestDispositionView({ pipelineOn, disposition, dispositionView, filters })
  const savedListRefetchTrigger = pipelineOn ? refetchTrigger : 0

  const enrichedFilterDefs = useMemo(() => {
    const defs = [...(filterData.filters || [])]
    if (!defs.some(f => f.value === 'userStatuses')) {
      defs.push({ value: 'userStatuses', label: 'Status' })
    }
    return defs
  }, [filterData.filters])

  const enrichedUtilDefs = useMemo(() => {
    const utils = [...(filterData.utils || [])]
    userStatuses.forEach(s => {
      if (!utils.some(u => u.value === s._id)) {
        utils.push({ value: s._id, label: s.label })
      }
    })
    return utils
  }, [filterData.utils, userStatuses])

  const [requestFilters, setRequestFilters] = useState(() =>
    filters.length > 0 ? filters : [initQueryData]
  )

  useEffect(() => {
    setRequestFilters(filters.length > 0 ? filters : [initQueryData])
  }, [filtersKey])

  const isFetching = useRef(false)
  const requestCounter = useRef(0)
  const requestedView = useRef(undefined)

  useEffect(() => {
    const loadCurrentUser = async () => {
      const user = await StorageGetItem(USER)
      setCurrentUser(user)
    }
    loadCurrentUser()

    const cachedStatuses = getListCache('leads:statusOptions')
    if (cachedStatuses) {
      setUserStatuses(cachedStatuses)
      setStatusesLoaded(true)
    }
    const cachedDispositions = getListCache('leads:dispositionOptions')
    if (cachedDispositions) {
      setLeadDispositions(cachedDispositions)
      setDispositionsLoaded(true)
    }

    dispatch(getContactStatuses()).then(res => {
      const statuses = (res?.payload?.data || []).filter(s => LEAD_STATUS_VALUES.includes(s.value))
      const mapped = statuses.map(s => ({ _id: s._id, label: s.label, value: s.value, bgColor: s.bgColor, textColor: s.textColor }))
      setUserStatuses(mapped)
      setStatusesLoaded(true)
      setListCache('leads:statusOptions', mapped)
    })
    dispatch(getLeadDispositions()).then(res => {
      if (res?.payload?.data) {
        const mapped = res.payload.data.map(d => ({ _id: d._id, label: d.label, value: d.value, bgColor: d.bgColor, textColor: d.textColor }))
        setLeadDispositions(mapped)
        setDispositionsLoaded(true)
        setListCache('leads:dispositionOptions', mapped)
      }
    })
  }, [])

  const leadsCacheGuard = useRef(true)
  useEffect(() => {
    if (leadsCacheGuard.current) {
      leadsCacheGuard.current = false
      return
    }
    clearListCache('leads')
  }, [handleDelete, refetchTrigger])

  // Fetch contacts - dependencies are primitives only
  useEffect(() => {
    // Skip if filterListId is set (mutually exclusive)
    if (filterListId) return
    if (isFetching.current && requestedView.current === viewParam) return

    const requestNo = ++requestCounter.current
    requestedView.current = viewParam
    const cacheKey = buildCacheKey('leads', { page, limit, search, filtersKey, sortField, sortOrder, ...(viewParam ? { viewParam } : {}) })

    const applyData = ({ users, pageCount, totalCount, statusCounts, dispositionCounts }) => {
      setContacts(users)
      setPageCount(pageCount)
      setTotalCount(totalCount)
      setStatusCounts(statusCounts)
      setDispositionCounts(dispositionCounts)
      setCountsLoaded(true)
    }

    const fetchData = async () => {
      isFetching.current = true
      const cached = getListCache(cacheKey)
      if (cached) {
        applyData(cached)
        setContactsLoading(false)
      } else {
        setContactsLoading(true)
      }
      triggerBar(true)
      try {
        const { role } = await StorageGetItem(USER)
        const offset = (page - 1) * limit
        const currentFilters = filters.length > 0 ? filters : [initQueryData]
        const apiFilters = currentFilters[0]?.filterType?.length > 0 ? currentFilters : ""

        const response = await dispatch(fetchContactsReq({
          limit,
          offset,
          filters: apiFilters,
          searchQuery: search,
          leads: true,
          byCreator: role !== 'customer',
          sortField,
          sortOrder,
          ...(viewParam ? { dispositionView: viewParam } : {}),
        }))
        if (requestNo !== requestCounter.current) {
          triggerBar(false)
          return
        }
        if (listStrictRef.current && response?.error) {
          showSnackBar("error", typeof response.payload === 'string' ? response.payload : 'Failed to fetch leads')
          setContactsLoading(false)
          triggerBar(false)
          isFetching.current = false
          return
        }
        const counts = response?.payload?.data?.meta?.statusCounts || {}
        const leadTotal = LEAD_STATUS_VALUES.reduce((sum, v) => sum + (counts[v] || 0), 0)
        const fresh = {
          users: response?.payload?.data?.users,
          pageCount: response?.payload?.data?.meta?.pageCount,
          totalCount: response?.payload?.data?.meta?.totalCount,
          statusCounts: { ...counts, total: leadTotal },
          dispositionCounts: response?.payload?.data?.meta?.dispositionCounts || {},
        }
        applyData(fresh)
        setListCache(cacheKey, fresh)
      } catch (error) {
        showSnackBar("error", error)
      }
      setContactsLoading(false)
      triggerBar(false)
      isFetching.current = false
    }

    fetchData()
  }, [page, limit, search, filtersKey, filterListId, sortField, sortOrder, handleDelete, refetchTrigger, viewParam, dispatch])

  useEffect(() => {
    if (!filterListId) return
    if (isFetching.current && requestedView.current === viewParam) return

    const requestNo = ++requestCounter.current
    requestedView.current = viewParam

    const fetchData = async () => {
      isFetching.current = true
      setContactsLoading(true)
      triggerBar(true)
      try {
        const { role } = await StorageGetItem(USER)
        const offset = (page - 1) * limit

        const response = await dispatch(getContactByFilterList({
          filterListId,
          limit,
          offset,
          searchQuery: search,
          leads: true,
          byCreator: role !== 'customer',
          sortField,
          sortOrder,
          ...(viewParam ? { dispositionView: viewParam } : {}),
        }))
        if (requestNo !== requestCounter.current) {
          triggerBar(false)
          return
        }
        if (listStrictRef.current && response?.error) {
          showSnackBar("error", typeof response.payload === 'string' ? response.payload : 'Failed to fetch leads')
          setContactsLoading(false)
          triggerBar(false)
          isFetching.current = false
          return
        }
        setContacts(response?.payload?.data?.users)
        setPageCount(response?.payload?.data?.meta?.pageCount)
        setTotalCount(response?.payload?.data?.meta?.totalCount)
        const counts = response?.payload?.data?.meta?.statusCounts || {}
        const leadTotal = LEAD_STATUS_VALUES.reduce((sum, v) => sum + (counts[v] || 0), 0)
        setStatusCounts({ ...counts, total: leadTotal })
        setDispositionCounts(response?.payload?.data?.meta?.dispositionCounts || {})
        setCountsLoaded(true)
      } catch (error) {
        showSnackBar("error", error)
        console.error('An unexpected error occurred:', error)
      }
      setContactsLoading(false)
      triggerBar(false)
      isFetching.current = false
    }

    fetchData()
  }, [page, limit, search, filterListId, sortField, sortOrder, handleDelete, savedListRefetchTrigger, viewParam, dispatch])

  // Handler for search (resets page, preserves saved filter)
  const handleSearch = useCallback((value) => {
    if (value?.length >= 3 || value?.length === 0) {
      setMultiple({
        search: value,
        page: 1,
      })
    }
  }, [setMultiple])

  // Handler for saved filter selection
  const handleFilterListSelect = useCallback((id) => {
    setMultiple({
      filterListId: id,
      filters: [],
      page: 1,
    })
  }, [setMultiple])

  // Handler for pagination
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage)
  }, [setPage])

  const handleLimitChange = useCallback((newLimit) => {
    setMultiple({
      limit: newLimit,
      page: 1,
    })
  }, [setMultiple])

  const applyFilters = useCallback((newFilters) => {
    setRequestFilters(newFilters)
    setMultiple({
      filters: newFilters[0]?.filterType?.length > 0 ? newFilters : [],
      filterListId: '',
      page: 1,
    })
  }, [setMultiple])

  // Trigger refetch without changing filters (used for delete etc.)
  const triggerRefetch = useCallback(() => {
    setHandleDelete(Date.now().toString())
  }, [])

  const handleSort = useCallback((field, order) => {
    setMultiple({
      sortField: field,
      sortOrder: order,
      page: 1,
    })
  }, [setMultiple])

  const handleStatusChange = (statusValue) => {
    if (statusValue === '') {
      const newFilters = requestFilters.filter(f => f.filterType !== 'userStatuses')
      setMultiple({
        status: '',
        filters: newFilters.length > 0 ? newFilters : [],
        page: 1,
      })
      setRequestFilters(newFilters.length > 0 ? newFilters : [initQueryData])
    } else {
      const statusObj = userStatuses.find(s => s.value === statusValue)
      const statusId = statusObj?._id || statusValue
      const statusFilter = { fieldName: 'userStatuses', operator: [statusId], value: '', filterType: 'userStatuses' }
      const otherFilters = requestFilters.filter(f => f.filterType && f.filterType !== 'userStatuses')
      const newFilters = [...otherFilters, statusFilter]
      setMultiple({
        status: statusValue,
        filters: newFilters,
        page: 1,
      })
      setRequestFilters(newFilters)
    }
  }

  const handleDispositionChange = (dispositionValue, extra = {}) => {
    if (dispositionValue === '') {
      const newFilters = requestFilters.filter(f => f.filterType !== 'leadDisposition')
      setMultiple({
        disposition: '',
        filters: newFilters.length > 0 ? newFilters : [],
        page: 1,
        ...extra,
      })
      setRequestFilters(newFilters.length > 0 ? newFilters : [initQueryData])
    } else {
      const dispObj = leadDispositions.find(d => d.value === dispositionValue)
      const dispId = dispObj?._id || dispositionValue
      const dispFilter = { fieldName: 'leadDisposition', operator: [dispId], value: '', filterType: 'leadDisposition' }
      const otherFilters = requestFilters.filter(f => f.filterType && f.filterType !== 'leadDisposition')
      const newFilters = [...otherFilters, dispFilter]
      setMultiple({
        disposition: dispositionValue,
        filters: newFilters,
        page: 1,
        ...extra,
      })
      setRequestFilters(newFilters)
    }
  }

  const handlePipelineTabChange = (tabValue) => {
    if (tabValue === ACTIVE_VIEW_TAB || tabValue === ALL_VIEW_TAB) {
      handleDispositionChange('', {
        dispositionView: tabValue === ALL_VIEW_TAB ? ALL_VIEW : '',
        filters: requestFilters.filter(f => f.filterType && f.filterType !== 'leadDisposition'),
      })
      return
    }
    handleDispositionChange(tabValue === disposition ? '' : tabValue)
  }

  const pipelineLeadingTabs = [
    { label: 'Active', value: ACTIVE_VIEW_TAB, count: dispositionCounts.active || 0 },
    { label: 'All', value: ALL_VIEW_TAB, count: dispositionCounts.total || 0 },
  ]

  const pipelineActiveTab = disposition
    || (hasLeadStatusFilter(filters) ? null : (dispositionView === ALL_VIEW ? ALL_VIEW_TAB : ACTIVE_VIEW_TAB))

  useEffect(() => {
    const statusFilter = filters.find(f => f.filterType === 'userStatuses')
    if (statusFilter && Array.isArray(statusFilter.operator) && statusFilter.operator.length === 1) {
      const statusId = statusFilter.operator[0]
      const statusObj = userStatuses.find(s => s._id === statusId)
      const statusValue = statusObj?.value || statusId
      if (status !== statusValue) {
        setMultiple({ status: statusValue })
      }
    } else if (!statusFilter && status !== '') {
      setMultiple({ status: '' })
    }

    const dispFilter = filters.find(f => f.filterType === 'leadDisposition')
    if (dispFilter && Array.isArray(dispFilter.operator) && dispFilter.operator.length === 1) {
      const dispId = dispFilter.operator[0]
      const dispObj = leadDispositions.find(d => d._id === dispId)
      const dispValue = dispObj?.value || dispId
      if (disposition !== dispValue) {
        setMultiple({ disposition: dispValue })
      }
    } else if (!dispFilter && disposition !== '') {
      setMultiple({ disposition: '' })
    }
  }, [filtersKey])

  const handleExportCsv = async () => {
    setIsExporting(true)
    try {
      if (filterListId) {
        await downloadCustomersCsv({
          filterListId,
          searchQuery: search,
          sortField,
          sortOrder,
          leads: true,
        })
      } else {
        // Same massaging the list fetch applies before calling the API
        const currentFilters = filters.length > 0 ? filters : [initQueryData]
        const hasFilters = currentFilters.some(f => f.filterType?.length > 0)
        const apiFilters = hasFilters ? currentFilters.filter(f => f.filterType?.length > 0) : ""

        const params = {
          searchQuery: search,
          sortField,
          sortOrder,
          leads: true,
        }
        if (Array.isArray(apiFilters) && apiFilters.length > 0) {
          params.filters = apiFilters
        }
        await downloadCustomersCsv(params)
      }
    } catch (error) {
      showSnackBar("error", error?.message || "Export failed — please try again")
    } finally {
      setIsExporting(false)
    }
  }

  const copyEnrollmentLink = () => {
    if (!currentUser?._id) {
      showSnackBar("error", "Unable to generate enrollment link")
      return
    }
    const baseUrl = window.location.origin
    const affiliateId = handleTableId(currentUser._id)
    // Carry the office the link was generated from, so a prospect of a multi-office
    // affiliate is never asked to pick one they have no way to choose between.
    const activeOfficeId = localStorage.getItem('activeOfficeId')
    const officeQuery = activeOfficeId ? `?office=${encodeURIComponent(activeOfficeId)}` : ''
    const link = `${baseUrl}/enrollment/${affiliateId}${officeQuery}`
    navigator.clipboard.writeText(link)
    showSnackBar("success", "Enrollment link copied to clipboard")
  }

  return (
    <div className="">
      <ContainerPage>
        <div className="flex justify-between items-center">
          <PageTitle title="Leads List" />
          <div className="flex gap-4">
            {currentUser?.role === 'customer' && (
              <Button
                classes="flex h-9 items-center bg-surface"
                type="outlined"
                disabled={isExporting}
                onClick={handleExportCsv}
              >
                {isExporting ? 'Exporting…' : 'Export to CSV'}
              </Button>
            )}
            <Button
              classes="flex h-9 items-center bg-surface"
              type="outlined"
              onClick={copyEnrollmentLink}
            >
              Self Enrollment
            </Button>
            <Button classes="flex h-9 items-center" onClick={() => navigate("/contact/add-contact", {
              replace: true,
              state: { isCreate: true }
            })}>
              <p className={`flex `}>
                <img src={PlusIcon} alt="plus" />
                <p>Add Contact</p>
              </p>
            </Button>
          </div>
        </div>
        <div className={TABLE_CARD}>
          {/* Row 1 — utilities: search (left) · filters + reset (right) */}
          <div className="flex items-center justify-between gap-4 px-5 pt-4 pb-3 flex-wrap">
            <DebounceInput customClassName={'b-1'} value={search} setSearchValue={handleSearch}
                           placeholder="Search leads..."/>
            <div className="flex items-center gap-3 flex-wrap">
              <Filters fetchByFilterList={handleFilterListSelect} fetchByFilters={applyFilters}
                       requestFilters={requestFilters} flow='contacts'
                       filterListId={filterListId} setFilterListId={handleFilterListSelect}
                       onFilterDataLoaded={setFilterData} />
              {(() => {
                const advCount = requestFilters.filter(f => (f.fieldName || f.operator || f.value) && f.filterType !== 'userStatuses' && f.filterType !== 'leadDisposition').length
                const totalActive = advCount + (status ? 1 : 0) + (disposition ? 1 : 0) + (search ? 1 : 0)
                if (totalActive === 0) return null
                return (
                  <>
                    <span className="h-6 w-px bg-divider" />
                    <span className="text-sm text-text-secondary whitespace-nowrap">{totalActive} filter{totalActive > 1 ? 's' : ''} applied</span>
                    <button
                      type="button"
                      onClick={() => {
                        // Every axis is cleared in ONE setMultiple write. Chaining
                        // handleStatusChange('')/handleDispositionChange('') after the
                        // filters wipe re-read the stale requestFilters closure and
                        // wrote the param straight back, so Reset left the list filtered.
                        setRequestFilters([initQueryData])
                        setMultiple({ filters: [], status: '', disposition: '', search: '', page: 1, ...(pipelineOn ? { dispositionView: '' } : {}) })
                      }}
                      className="flex items-center gap-1.5 text-sm font-medium text-primary-main hover:opacity-80 whitespace-nowrap"
                    >
                      <LuRotateCw size={16} /> Reset
                    </button>
                  </>
                )
              })()}
            </div>
          </div>

          {/* Row 2 — status axes: segmented top-level scope (single) · seam · disposition pills (quick) */}
          <div className="flex items-center gap-5 px-5 pb-3 flex-wrap">
            {(statusesLoaded && countsLoaded) ? (
              <div className="inline-flex items-stretch rounded-lg border border-divider overflow-hidden shrink-0">
                {[{ label: 'Total Leads', value: '', count: statusCounts.total || 0, color: null },
                  ...userStatuses.map(s => ({ label: s.label, value: s.value, count: statusCounts[s.value] || 0, color: s.textColor }))
                ].map(seg => {
                  const active = status === seg.value
                  return (
                    <button
                      key={seg.label}
                      onClick={() => handleStatusChange(active ? '' : seg.value)}
                      className={`flex items-center gap-2 px-3.5 h-9 text-sm font-medium border-r border-divider last:border-r-0 transition-colors ${active ? '' : 'hover:bg-[var(--color-gray-150)]'}`}
                      style={active ? { backgroundColor: 'rgba(94,106,210,0.14)' } : undefined}
                    >
                      {seg.color && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />}
                      {/* the active label is brand indigo AS TEXT, so it takes the text
                          token — the fill value measured 3.15:1 on the active tint */}
                      <span style={{ color: active ? 'var(--color-primary-text)' : 'var(--color-text-primary)' }}>{seg.label}</span>
                      <span className="text-text-secondary">{seg.count}</span>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className="h-9 w-[360px] rounded-lg border border-divider animate-pulse shrink-0" />
            )}

            <span className="h-6 w-px bg-divider shrink-0" />

            <div className="flex items-center gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
              {pipelineOn ? (
                <StatusTabs
                  counts={dispositionCounts}
                  statuses={filterListId ? [] : leadDispositions}
                  leadingTabs={pipelineLeadingTabs}
                  activeStatus={pipelineActiveTab}
                  onStatusChange={handlePipelineTabChange}
                  loading={!dispositionsLoaded || !countsLoaded}
                  skeletonCount={10}
                  hideTotal
                />
              ) : (
                <StatusTabs
                  counts={dispositionCounts}
                  statuses={leadDispositions}
                  activeStatus={disposition}
                  onStatusChange={(v) => handleDispositionChange(v === disposition ? '' : v)}
                  loading={!dispositionsLoaded || !countsLoaded}
                  skeletonCount={6}
                  hideTotal
                />
              )}
            </div>
          </div>

          {/* Row 3 — applied advanced-filter chips */}
          <div className="px-5">
            <AppliedFilters
              requestFilters={requestFilters.filter(f => f.filterType !== 'userStatuses' && f.filterType !== 'leadDisposition')}
              filterDefs={enrichedFilterDefs} utilDefs={enrichedUtilDefs}
              onRemove={(index) => {
                const adv = requestFilters.filter(f => f.filterType !== 'userStatuses' && f.filterType !== 'leadDisposition' && (f.fieldName || f.operator || f.value))
                const target = adv[index]
                const updated = requestFilters.filter(f => f !== target)
                const newFilters = updated.length > 0 ? updated : [initQueryData]
                setRequestFilters(newFilters)
                setMultiple({ filters: updated.filter(f => f.fieldName || f.operator || f.value), page: 1 })
              }}
              onClearAll={() => {
                const kept = requestFilters.filter(f => f.filterType === 'userStatuses' || f.filterType === 'leadDisposition')
                const newFilters = kept.length > 0 ? kept : [initQueryData]
                setRequestFilters(newFilters)
                setMultiple({ filters: kept, page: 1 })
              }}
            />
          </div>
          <TableContactList data={contacts} setHandleDelete={setHandleDelete}
                            loading={contactsLoading}
                            skeletonRows={limit}
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                            showDebtAmount={debtAmountOn}
                            triggerUpdate={() => setRefetchTrigger(t => t + 1)}/>
          <TableFooter page={page} limit={limit} total={totalCount} pageCount={pageCount}
            setPage={handlePageChange} setLimit={handleLimitChange} />
        </div>
      </ContainerPage>
    </div>
  )
}

export default Leads
